import { useGlobalLoader } from "@/components/GlobalLoader";
import impressao_socket_api from "@/services/impressao_socket_api";
import { type FilaSocketResponse } from "@/types/fila-socket-response";
import { type ItemFilaSocketResponse } from "@/types/item-fila-socket-response";
import { createContext, useContext, useState, type ReactNode } from "react";
import { useImpressaoConfig } from "../ImpressaoConfigContext";

export type FilaImpressaoSocketContextType = {
  coletarItemFila: () => Promise<void>;
  itensFila: ItemFilaSocketResponse[];
  apagarFilaImpressao: (nome_da_ordem: string) => Promise<void>;
};

export const FilaImpressaoSocketContext = createContext<
  FilaImpressaoSocketContextType | undefined
>(undefined);

export function FilaImpressaoSocketProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { impressaoConfig } = useImpressaoConfig();
  const { setIsLoading } = useGlobalLoader();
  const [itensFila, setItensFila] = useState<ItemFilaSocketResponse[]>([]);

  const apagarFilaImpressao = async (nome_da_ordem: string) => {
    if (!impressaoConfig?.app_id) return;
    setIsLoading(true);
    try {
      if (nome_da_ordem.length > 0) {
        await impressao_socket_api.post(`/v1/delete-item-fila`, {
          id_modulo: impressaoConfig.app_id,
          nome_da_ordem: nome_da_ordem,
          id_conta: impressaoConfig.id_conta,
        });
      } else {
        await impressao_socket_api.post(`/v1/delete-item-fila`, {
          id_modulo: impressaoConfig.app_id,
        });
      }
      setItensFila([]);
    } catch (error) {
      console.error(
        "Erro ao apagar toda a fila de impressão via socket:",
        error
      );
    } finally {
      setIsLoading(false);
    }
  };

  const coletarItemFila = async () => {
    if (!impressaoConfig?.app_id) return;
    setIsLoading(true);
    try {
      const response_fila_socket =
        await impressao_socket_api.get<FilaSocketResponse>(
          `/v1/item-fila/${impressaoConfig.app_id}?pagina=1&quantidade_por_pagina=-1`
        );
      setItensFila(response_fila_socket.data.itens);
    } catch (error) {
      console.error(
        "Erro ao coletar itens da fila de impressão via socket:",
        error
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FilaImpressaoSocketContext.Provider
      value={{ itensFila, coletarItemFila, apagarFilaImpressao }}
    >
      {children}
    </FilaImpressaoSocketContext.Provider>
  );
}

export function useFilaImpressaoSocket() {
  const context = useContext(FilaImpressaoSocketContext);
  if (!context) {
    throw new Error(
      "useFilaImpressaoSocket deve ser usado dentro de um FilaImpressaoSocketProvider"
    );
  }
  return context;
}
